/**
 * Social share intent URLs + Open Graph / Twitter meta helpers.
 * Keep free of package imports so Functions can copy this file at build.
 */

export type SocialShareInput = {
  url: string;
  title: string;
  description?: string;
  imageUrl?: string | null;
};

export type SocialShareIntents = {
  x: string;
  facebook: string;
  linkedin: string;
  pinterest: string;
  whatsapp: string;
};

export const OG_SITE_NAME = 'Containd Spaces';

export function absolutizeUrl(url: string, origin: string): string {
  const href = String(url || '').trim();
  if (!href) return '';
  if (/^https?:\/\//i.test(href) || href.startsWith('data:') || href.startsWith('blob:')) {
    return href;
  }
  const base = String(origin || '').replace(/\/+$/, '');
  if (!base) return href;
  if (href.startsWith('//')) return `https:${href}`;
  if (href.startsWith('/')) return `${base}${href}`;
  return `${base}/${href}`;
}

export function socialShareIntents(input: SocialShareInput): SocialShareIntents {
  const url = encodeURIComponent(input.url);
  const title = encodeURIComponent(input.title || '');
  const description = encodeURIComponent(
    (input.description || input.title || '').trim(),
  );
  const media = input.imageUrl ? encodeURIComponent(input.imageUrl) : '';
  const whatsappText = encodeURIComponent(
    [input.title, input.url].filter(Boolean).join(' '),
  );
  return {
    x: `https://twitter.com/intent/tweet?text=${title}&url=${url}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    pinterest: media
      ? `https://www.pinterest.com/pin/create/button/?url=${url}&media=${media}&description=${description}`
      : `https://www.pinterest.com/pin/create/button/?url=${url}&description=${description}`,
    whatsapp: `https://wa.me/?text=${whatsappText}`,
  };
}

export function pinterestPinIntent(opts: {
  pageUrl: string;
  mediaUrl: string;
  description: string;
}): string {
  const url = encodeURIComponent(opts.pageUrl);
  const media = encodeURIComponent(opts.mediaUrl);
  const description = encodeURIComponent(opts.description || '');
  return `https://www.pinterest.com/pin/create/button/?url=${url}&media=${media}&description=${description}`;
}

function escAttr(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export type OpenGraphInput = {
  type?: 'website' | 'profile' | 'article';
  siteName?: string;
  title: string;
  description: string;
  url: string;
  locale?: string;
  imageUrl?: string | null;
  imageAlt?: string | null;
  imageWidth?: number | null;
  imageHeight?: number | null;
  profileUsername?: string | null;
};

/** Open Graph + Twitter Card tags for crawler / messenger previews. */
export function renderSocialMetaTags(input: OpenGraphInput): string {
  const type = input.type || 'website';
  const siteName = input.siteName || OG_SITE_NAME;
  const locale = input.locale || 'en';
  const lines = [
    `<meta property="og:type" content="${escAttr(type)}"/>`,
    `<meta property="og:site_name" content="${escAttr(siteName)}"/>`,
    `<meta property="og:title" content="${escAttr(input.title)}"/>`,
    `<meta property="og:description" content="${escAttr(input.description)}"/>`,
    `<meta property="og:url" content="${escAttr(input.url)}"/>`,
    `<meta property="og:locale" content="${escAttr(locale)}"/>`,
  ];
  if (input.profileUsername) {
    lines.push(
      `<meta property="profile:username" content="${escAttr(input.profileUsername)}"/>`,
    );
  }
  if (input.imageUrl) {
    lines.push(`<meta property="og:image" content="${escAttr(input.imageUrl)}"/>`);
    if (input.imageAlt) {
      lines.push(
        `<meta property="og:image:alt" content="${escAttr(input.imageAlt)}"/>`,
      );
    }
    if (input.imageWidth && input.imageWidth > 0) {
      lines.push(
        `<meta property="og:image:width" content="${String(Math.round(input.imageWidth))}"/>`,
      );
    }
    if (input.imageHeight && input.imageHeight > 0) {
      lines.push(
        `<meta property="og:image:height" content="${String(Math.round(input.imageHeight))}"/>`,
      );
    }
  }
  lines.push('<meta name="twitter:card" content="summary_large_image"/>');
  lines.push(`<meta name="twitter:title" content="${escAttr(input.title)}"/>`);
  lines.push(
    `<meta name="twitter:description" content="${escAttr(input.description)}"/>`,
  );
  if (input.imageUrl) {
    lines.push(
      `<meta name="twitter:image" content="${escAttr(input.imageUrl)}"/>`,
    );
    if (input.imageAlt) {
      lines.push(
        `<meta name="twitter:image:alt" content="${escAttr(input.imageAlt)}"/>`,
      );
    }
  }
  return lines.join('\n');
}

/** Google Search Console site verification meta (when configured in publicSite). */
export function renderSearchConsoleVerificationMeta(
  token: string | null | undefined,
): string {
  const value = String(token || '').trim();
  if (!value) return '';
  return `<meta name="google-site-verification" content="${escAttr(value)}"/>`;
}
